/**
 * Lotteries module DTOs (§15.2): public lottery page, admin draft/version operations, lifecycle commands.
 * The public DTO exposes only published configuration — never seeds, unrevealed vectors, risk thresholds or
 * reviewer notes (§15.1, §5.4).
 */
import { z } from 'zod';
import { CountryCode, HexColor, HexHash256, IanaTimezone, IsoDateTime, LocaleTag, PositiveInt, Slug, Ulid, } from './common.js';
import { EnergyLevel, EngagementTaskType, EntryMode, FulfillmentType, LotteryMechanic, LotteryStatus, PrizeTier, SocialPlatform, SponsorTier, } from './enums.js';
import { SponsorPlacement } from './events.js';
import { ConfigVersion, HeroMediaSchema, LotteryConfigSchema, OddsDisclosureSchema, RequiredContact, RevealStyle, ThemeTokensSchema, } from './lottery-config.js';
import { MoneyDto } from './money.js';
// ---------------------------------------------------------------------------------------------------------
// Public (`GET /v1/l/:slug`)
// ---------------------------------------------------------------------------------------------------------
export const PublicSponsorDto = z.object({
    sponsorId: Ulid,
    name: z.string(),
    tier: SponsorTier,
    logoLightUrl: z.string().optional(),
    logoDarkUrl: z.string().optional(),
    brandColor: HexColor.optional(),
    tagline: z.string().optional(),
    websiteUrl: z.string().optional(),
    ctaLabel: z.string().optional(),
    ctaUrl: z.string().optional(),
    placements: z.array(SponsorPlacement),
    displayWeight: z.number().int(),
});
export const PublicPrizeDto = z.object({
    prizeId: Ulid,
    name: z.string(),
    description: z.string().optional(),
    tier: PrizeTier,
    quantity: z.number().int(),
    value: MoneyDto,
    fulfillmentType: FulfillmentType,
    sponsor: z.object({ sponsorId: Ulid, name: z.string() }).optional(),
    accentColor: HexColor.optional(),
    heroImageUrl: z.string().optional(),
    heroImageAlt: z.string().optional(),
    gallery: z.array(z.object({ url: z.string(), alt: z.string() })).default([]),
    /** Mode B: the cap and approved brand names are public (§6.1). */
    valueCap: MoneyDto.optional(),
    approvedBrands: z.array(z.string()).default([]),
});
export const PublicPlatformDto = z.object({
    platform: SocialPlatform,
    label: z.string(),
    accountUrl: z.string(),
    displayOrder: z.number().int(),
});
export const PublicTaskDto = z.object({
    taskId: Ulid,
    type: EngagementTaskType,
    title: z.string(),
    bonusEntries: z.number().int(),
    platform: SocialPlatform.optional(),
    destinationUrl: z.string().optional(),
    consentText: z.string(),
});
/** §8: what participants see about sponsor-default consolation — the exact product and the published range. */
export const PublicSamplingDto = z.object({
    sponsorName: z.string(),
    prizeName: z.string(),
    prizeValue: MoneyDto,
    rangeStart: z.number().int(),
    rangeEnd: z.number().int(),
    countingMode: z.enum(['QUALIFIED_ENTRY', 'UNIQUE_PARTICIPANT']),
    qualifyingEntryTypes: z.array(z.enum(['FREE', 'PAID', 'BONUS'])),
    copy: z.string(),
});
export const PublicScheduleDto = z.object({
    opensAt: IsoDateTime,
    closesAt: IsoDateTime,
    drawAt: IsoDateTime.optional(),
    resultsPublishAt: IsoDateTime.optional(),
    timezone: IanaTimezone,
    /** Server instant the payload was rendered at: the client derives countdowns from this, never from its clock. */
    serverNow: IsoDateTime,
});
/** Public mechanic facts: counts and ranges only. Pool vectors, seeds and liabilities stay server-side. */
export const PublicMechanicDto = z.discriminatedUnion('mechanic', [
    z.object({
        mechanic: z.literal('SCHEDULED_RAFFLE'),
        tiers: z.array(z.object({ tier: PrizeTier, prizeId: Ulid, winners: z.number().int() })),
    }),
    z.object({
        mechanic: z.literal('INSTANT_WIN'),
        poolSize: z.number().int(),
        winningTickets: z.number().int(),
    }),
    z.object({
        mechanic: z.literal('SPIN_WHEEL'),
        poolSize: z.number().int(),
        winningTickets: z.number().int(),
        segments: z.array(z.object({ label: z.string(), prizeId: Ulid.nullable() })).optional(),
    }),
    z.object({
        mechanic: z.literal('SCRATCH_CARD'),
        poolSize: z.number().int(),
        winningTickets: z.number().int(),
    }),
    z.object({
        mechanic: z.literal('NUMBER_PICK'),
        range: z.object({ min: z.number().int(), max: z.number().int() }),
        pickCount: z.number().int(),
        quickPick: z.enum(['ALLOWED', 'REQUIRED', 'DISABLED']),
        matchTiers: z.array(z.object({ matches: z.number().int(), prizeId: Ulid, prizeUnits: z.number().int() })),
        maxLiability: MoneyDto,
    }),
    z.object({
        mechanic: z.literal('FIRST_N'),
        countingMode: z.enum(['QUALIFIED_ENTRY', 'UNIQUE_PARTICIPANT']),
        milestones: z.array(z.object({ position: z.number().int(), prizeId: Ulid })).optional(),
        everyNth: z.object({ n: z.number().int(), prizeId: Ulid, limit: z.number().int() }).optional(),
    }),
]);
export const PublicLotteryDto = z.object({
    lotteryId: Ulid,
    slug: Slug,
    status: LotteryStatus,
    configVersion: ConfigVersion,
    termsVersion: z.string(),
    privacyVersion: z.string(),
    locale: LocaleTag,
    locales: z.array(LocaleTag),
    currency: z.string(),
    organizer: z.object({ name: z.string(), logoUrl: z.string().optional() }),
    title: z.string(),
    prizeStatement: z.string(),
    howSelectionWorks: z.string().optional(),
    sponsorStory: z.string().optional(),
    faq: z.array(z.object({ q: z.string(), a: z.string() })),
    rulesUrl: z.string().optional(),
    privacyUrl: z.string().optional(),
    supportRoute: z.string(),
    mechanic: LotteryMechanic,
    /** For FREE_GIVEAWAY, the wrapped mechanic facts. */
    mechanicDetails: PublicMechanicDto,
    entry: z.object({
        mode: EntryMode,
        price: MoneyDto,
        requiredContact: RequiredContact,
        quantity: z.object({ min: z.number().int(), max: z.number().int() }),
        caps: z.object({ perPerson: z.number().int(), perDay: z.number().int().optional() }),
        bonusEntryCap: z.number().int(),
        cancellationPolicy: z.string(),
        refundRule: z.string(),
    }),
    eligibility: z.object({
        minAge: z.number().int(),
        countriesAllowed: z.array(z.string()),
        countriesDenied: z.array(z.string()),
        residencyRequired: z.boolean(),
        exclusions: z.array(z.string()),
        employeesExcluded: z.boolean(),
    }),
    schedule: PublicScheduleDto,
    odds: OddsDisclosureSchema.omit({ statement: true }).extend({ statement: z.string() }),
    prizes: z.array(PublicPrizeDto),
    sponsors: z.array(PublicSponsorDto),
    sampling: z.array(PublicSamplingDto),
    platforms: z.array(PublicPlatformDto),
    tasks: z.array(PublicTaskDto),
    presentation: z.object({
        theme: ThemeTokensSchema,
        prizeAccents: z.record(Ulid, HexColor),
        heroMedia: HeroMediaSchema.extend({ url: z.string() }),
        revealStyle: RevealStyle,
        energyLevel: EnergyLevel,
        soundDefault: z.literal(false),
        sponsorPlacements: z.array(SponsorPlacement),
    }),
    claims: z.object({
        claimWindowDays: z.number().int(),
        variantHoldMinutes: z.number().int(),
        unclaimedPrizePolicy: z.string(),
    }),
    /** Real aggregate counts only (§13.1 social proof). */
    stats: z.object({ confirmedEntries: z.number().int(), uniqueParticipants: z.number().int().optional() }),
    /** §9.1 step 1: published seed commitment with its trusted timestamp. */
    fairness: z
        .object({
        algorithm: z.string(),
        seedCommit: HexHash256,
        committedAt: IsoDateTime,
        publishedTermsHash: HexHash256,
    })
        .optional(),
    /** Market gate (§4.2 A "unavailable-by-market"). */
    availableInMarket: z.boolean(),
});
/** Keys that must never appear anywhere in a public payload (tests walk the object tree). */
export const PUBLIC_PAYLOAD_FORBIDDEN_KEYS = [
    'seed',
    'revealedSeed',
    'serverSeed',
    'poolVector',
    'outcomeVector',
    'ticketVector',
    'riskThreshold',
    'riskThresholds',
    'riskRules',
    'reviewerNotes',
    'adminNotes',
    'adminCommentary',
    'claimToken',
    'providerSecret',
    'webhookSecret',
];
// ---------------------------------------------------------------------------------------------------------
// Admin (`/v1/admin/lotteries...`)
// ---------------------------------------------------------------------------------------------------------
export const CreateLotteryRequest = z.object({
    templateId: z.string().max(64).optional(),
    config: LotteryConfigSchema.omit({ configVersion: true }),
});
export const UpdateLotteryRequest = z.object({
    /** Optimistic concurrency: the version the editor loaded. */
    expectedConfigVersion: ConfigVersion,
    config: LotteryConfigSchema.omit({ configVersion: true }),
    /** Required after the first entry: the participant remedy for a material change (§5.4). */
    changeNote: z.string().max(2000).optional(),
    participantRemedy: z.string().max(2000).optional(),
});
export const ConfigIssueDto = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
    severity: z.enum(['error', 'warning']),
});
export const LotteryAdminDto = z.object({
    lotteryId: Ulid,
    tenantId: Ulid,
    slug: Slug,
    status: LotteryStatus,
    mechanic: LotteryMechanic,
    configVersion: ConfigVersion,
    publishedConfigVersion: ConfigVersion.nullable(),
    config: LotteryConfigSchema,
    /** True once any entry exists: rules lock, changes create a new version (§5.4). */
    hasEntries: z.boolean(),
    createdBy: Ulid,
    approvedBy: Ulid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    publishedAt: IsoDateTime.nullable(),
    seedCommit: HexHash256.nullable(),
    validation: z.object({ ok: z.boolean(), issues: z.array(ConfigIssueDto), liabilityMinor: z.string() }),
});
export const LotteryListQuery = z.object({
    status: LotteryStatus.optional(),
    mechanic: LotteryMechanic.optional(),
    q: z.string().max(80).optional(),
});
export const LotteryListRowDto = LotteryAdminDto.pick({
    lotteryId: true,
    slug: true,
    status: true,
    mechanic: true,
    configVersion: true,
    publishedConfigVersion: true,
    hasEntries: true,
    updatedAt: true,
    publishedAt: true,
}).extend({ title: z.string(), opensAt: IsoDateTime, closesAt: IsoDateTime, drawAt: IsoDateTime.optional() });
/** §5.5 simulation request: synthetic participants, never production inventory or ledgers. */
export const SimulateLotteryRequest = z.object({
    participants: PositiveInt.max(100_000).default(1000),
    paidShare: z.number().min(0).max(1).default(0.5),
    duplicateWebhooks: z.boolean().default(true),
    stockExhaustion: z.boolean().default(true),
    claimExpiry: z.boolean().default(true),
    seed: z.string().max(64).optional(),
});
export const SimulationReportDto = z.object({
    expectedEntries: z.number().int(),
    expectedUniqueParticipants: z.number().int(),
    maxPrizeLiability: MoneyDto,
    stockReconciliation: z.array(z.object({
        prizeId: Ulid,
        catalogStock: z.number().int(),
        mainReserved: z.number().int(),
        samplingReserved: z.number().int(),
        expectedWon: z.number().int(),
        available: z.number().int(),
    })),
    samplingAllocation: z.array(z.object({
        ruleId: Ulid,
        eligiblePositions: z.number().int(),
        expectedAwards: z.number().int(),
        mainWinnersInRange: z.number().int(),
    })),
    copyPreviews: z.array(z.object({ key: z.string(), locale: LocaleTag, text: z.string() })),
    validation: z.object({ ok: z.boolean(), issues: z.array(ConfigIssueDto) }),
    generatedAt: IsoDateTime,
});
export const SubmitForReviewRequest = z.object({ note: z.string().max(2000).optional() });
export const ApproveLotteryRequest = z.object({ note: z.string().max(2000).optional() });
export const PublishLotteryRequest = z.object({
    /** Second-person approval id for paid / high-value lotteries (§5.4). */
    approvalId: Ulid.optional(),
});
export const PauseLotteryRequest = z.object({ reason: z.string().min(3).max(2000) });
export const ResumeLotteryRequest = z.object({ note: z.string().max(2000).optional() });
export const CloseLotteryRequest = z.object({ reason: z.string().max(2000).optional() });
export const CancelLotteryRequest = z.object({
    reason: z.string().min(3).max(2000),
    /** §16.3 typed confirmation: the slug, retyped. */
    confirmSlug: Slug,
});
export const DrawLotteryRequest = z.object({
    /** Required for redraws (§9.4). */
    reason: z.string().max(2000).optional(),
    redrawOfDrawId: Ulid.optional(),
    /** Second operator for high-value draws (§9.5). */
    secondOperatorId: Ulid.optional(),
});
export const LifecycleTransitionResponse = z.object({
    lotteryId: Ulid,
    from: LotteryStatus,
    to: LotteryStatus,
    at: IsoDateTime,
    actorId: Ulid,
    auditId: Ulid,
});
/** §9.5 pre-draw gate results. */
export const DrawPrecheckDto = z.object({
    lotteryId: Ulid,
    ready: z.boolean(),
    gates: z.array(z.object({
        gate: z.enum(['CONFIGURATION', 'ENTRIES', 'INVENTORY', 'FAIRNESS', 'OPERATIONS', 'PUBLICATION']),
        ok: z.boolean(),
        detail: z.string(),
    })),
    counts: z.object({
        eligible: z.number().int(),
        refunded: z.number().int(),
        disqualified: z.number().int(),
        pending: z.number().int(),
    }),
    checkedAt: IsoDateTime,
});
// ---------------------------------------------------------------------------------------------------------
// Lotteries module extensions (lotteries agent): versions, share links, platforms, metrics, validation,
// public rules page. Appended per docs/AGENT_PROTOCOL.md (own DTO file, append-only).
// ---------------------------------------------------------------------------------------------------------
/** `GET /v1/admin/lotteries/:id/versions` item (§5.3 versioned contract, §5.4 change control). */
export const ConfigVersionDto = z.object({
    configVersion: ConfigVersion,
    config: LotteryConfigSchema,
    configHash: HexHash256,
    createdAt: IsoDateTime,
    createdBy: z.object({ staffId: Ulid, name: z.string() }).nullable(),
    changeNote: z.string().nullable(),
    participantRemedy: z.string().nullable(),
    published: z.boolean(),
    publishedAt: IsoDateTime.nullable(),
    supersededAt: IsoDateTime.nullable(),
    approvedBy: Ulid.nullable(),
    /** Validation report stored with the version at approve/publish time, when any. */
    validation: z.object({ ok: z.boolean(), issues: z.array(ConfigIssueDto) }).nullable(),
});
/** `POST /v1/admin/lotteries/:id/validate` — §5.4 publish-gate report with live stock. */
export const ValidateLotteryResponse = z.object({
    lotteryId: Ulid,
    configVersion: ConfigVersion,
    ok: z.boolean(),
    issues: z.array(ConfigIssueDto),
    liability: MoneyDto,
    /** Publish gates (§5.4): validation, inventory, terms, accessibility, market. */
    gates: z.array(z.object({
        gate: z.enum(['VALIDATION', 'INVENTORY', 'TERMS', 'ACCESSIBILITY', 'MARKET', 'DUAL_CONTROL']),
        ok: z.boolean(),
        detail: z.string(),
    })),
    /** Second-person approval needed before publish (paid or high-value, §5.4). */
    requiresDualControl: z.boolean(),
    stock: z.array(z.object({
        prizeId: Ulid,
        quantity: z.number().int(),
        guaranteedUnits: z.number().int(),
        samplingReservedUnits: z.number().int(),
        availableUnits: z.number().int().nullable(),
        reservedSamplingUnits: z.number().int().nullable(),
        ok: z.boolean(),
    })),
    checkedAt: IsoDateTime,
});
/** One shareable link per platform (§12.1 UTM source + referrer; §16.1 step 8 "shareable link per platform and QR"). */
export const ShareLinkDto = z.object({
    platform: SocialPlatform.or(z.literal('DIRECT')),
    label: z.string(),
    url: z.string(),
    /** Deep link into the client's own account on that platform, when configured. */
    accountUrl: z.string().optional(),
    utm: z.object({ source: z.string(), medium: z.string(), campaign: z.string() }),
});
export const ShareLinksDto = z.object({
    lotteryId: Ulid,
    slug: Slug,
    canonicalUrl: z.string(),
    /** `GET /v1/share/l/:slug` — HTML with Open Graph tags for crawlers. */
    shareUrl: z.string(),
    /** `GET /v1/share/l/:slug/card.png` — rendered share card. */
    cardUrl: z.string(),
    /** PNG data URL of the QR code pointing at the canonical participant page. */
    qrDataUrl: z.string(),
    links: z.array(ShareLinkDto),
    /** Null until the lottery is published. */
    publishedConfigVersion: ConfigVersion.nullable(),
});
/** Preconfigured platform row (`LotterySocialPlatform`, D06, §12.1). */
export const LotteryPlatformDto = z.object({
    platform: SocialPlatform,
    accountUrl: z.string(),
    accountLabel: z.string().nullable(),
    displayOrder: z.number().int(),
    isEnabled: z.boolean(),
});
export const LotteryPlatformsDto = z.object({ lotteryId: Ulid, items: z.array(LotteryPlatformDto) });
/** `PUT /v1/admin/lotteries/:id/platforms` — replaces the whole ordered list. */
export const PutLotteryPlatformsRequest = z.object({
    items: z
        .array(z.object({
        platform: SocialPlatform,
        accountUrl: z.url({ protocol: /^https?$/ }),
        accountLabel: z.string().max(60).nullable().optional(),
        displayOrder: z.number().int().min(0).optional(),
        isEnabled: z.boolean().default(true),
    }))
        .max(16),
});
/** `GET /v1/admin/lotteries/:id/metrics` — counts for the console overview (never substitutes zeros for unknowns). */
export const LotteryMetricsDto = z.object({
    lotteryId: Ulid,
    asOf: IsoDateTime,
    entries: z.object({
        total: z.number().int(),
        eligible: z.number().int(),
        pending: z.number().int(),
        refunded: z.number().int(),
        disqualified: z.number().int(),
        uniqueParticipants: z.number().int(),
    }),
    bySource: z.array(z.object({ source: z.enum(['FREE', 'PAID', 'BONUS']), count: z.number().int() })),
    byPlatform: z.array(z.object({ platform: SocialPlatform.or(z.literal('DIRECT')), count: z.number().int() })),
    wins: z.object({ total: z.number().int(), sponsorSamples: z.number().int() }).nullable(),
    claims: z.object({ open: z.number().int(), approved: z.number().int() }).nullable(),
    stock: z.array(z.object({
        prizeId: Ulid,
        available: z.number().int(),
        reservedSampling: z.number().int(),
        allocated: z.number().int(),
    })),
    schedule: z.object({
        status: LotteryStatus,
        opensAt: IsoDateTime.nullable(),
        closesAt: IsoDateTime.nullable(),
        drawAt: IsoDateTime.nullable(),
        secondsToClose: z.number().int().nullable(),
    }),
});
/** `GET /v1/l/:slug/rules` — the full versioned terms and privacy text shown with the published configuration. */
export const PublicRulesDto = z.object({
    slug: Slug,
    configVersion: ConfigVersion,
    title: z.string(),
    locale: LocaleTag,
    terms: z.object({
        version: z.string(),
        title: z.string(),
        body: z.string(),
        bodyHash: HexHash256,
        effectiveAt: IsoDateTime,
    }),
    privacy: z.object({
        version: z.string(),
        title: z.string(),
        body: z.string(),
        bodyHash: HexHash256,
        effectiveAt: IsoDateTime,
    }),
    /** Material facts as published (§4.2 A rules summary). */
    facts: z.object({
        entryMode: EntryMode,
        price: MoneyDto,
        minAge: z.number().int(),
        countriesAllowed: z.array(z.string()),
        closesAt: IsoDateTime,
        drawAt: IsoDateTime.optional(),
        timezone: IanaTimezone,
        oddsStatement: z.string(),
        claimWindowDays: z.number().int(),
        unclaimedPrizePolicy: z.string(),
        cancellationPolicy: z.string(),
        refundRule: z.string(),
        exclusions: z.array(z.string()),
        employeesExcluded: z.boolean(),
    }),
    /** Sponsor-default consolation disclosure (§8), when configured. */
    sampling: z.array(PublicSamplingDto),
    howSelectionWorks: z.string().optional(),
    faq: z.array(z.object({ q: z.string(), a: z.string() })),
    fairness: PublicLotteryDto.shape.fairness,
});
/** Extra simulator output (§5.5) for sequence/liability reconciliation; optional for older consumers. */
export const SimulationPathsDto = z.object({
    entries: z.object({
        free: z.number().int(),
        paid: z.number().int(),
        paymentFailed: z.number().int(),
        duplicateWebhooksIgnored: z.number().int(),
        latePaymentsExcluded: z.number().int(),
        confirmed: z.number().int(),
    }),
    outcomes: z.object({
        mainWins: z.number().int(),
        losses: z.number().int(),
        stockExhaustedTickets: z.number().int(),
        samplingAwards: z.number().int(),
        samplingSkippedMainWinner: z.number().int(),
        samplingSkippedOutOfRange: z.number().int(),
        samplingSkippedNoStock: z.number().int(),
    }),
    claims: z.object({ opened: z.number().int(), expired: z.number().int(), redrawn: z.number().int() }),
    cancellation: z.object({ refundsIssued: z.number().int(), refundTotal: MoneyDto }),
    sequence: z.object({
        lastParticipantPosition: z.number().int(),
        lastEntryPosition: z.number().int(),
        lastWinningPosition: z.number().int().nullable(),
        positionsUnique: z.boolean(),
    }),
    liability: z.object({
        configured: MoneyDto,
        simulatedWon: MoneyDto,
        withinCap: z.boolean(),
    }),
});
export const SimulationReportFullDto = SimulationReportDto.extend({
    seed: z.string(),
    participants: z.number().int(),
    paths: SimulationPathsDto,
    odds: z
        .object({ winningTickets: z.number().int(), poolSize: z.number().int(), display: z.string() })
        .nullable(),
});
/** Public preview query: staff may preview a draft/unpublished version (`?preview=<configVersion>`). */
export const PublicLotteryQuery = z.object({
    preview: z.coerce.number().int().min(1).optional(),
    /** ISO 3166-1 alpha-2 hint for the market gate (§4.2 A unavailable-by-market); the edge sets it from geo-IP. */
    country: CountryCode.optional(),
    locale: LocaleTag.optional(),
});
//# sourceMappingURL=lotteries.js.map